import { useState } from 'react';
import type { TeamsConnection } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useTeams, useTeamsMicrosoft, useTeamsMutations, useTeamsStatus } from '../../lib/api/hooks-teams.js';
import { TeamsPresenceBadge, teamsProblems } from './TeamsPresence.js';
import { ApiClientError } from '../../lib/api/client.js';
import { Badge, CardHead, Empty, ErrorBanner, SectionHead } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Microsoft Teams calling through ArcNet (docs/44): the steps the account's Microsoft 365 admin
 * takes, in order, with this account's own values, and which extensions ring in Teams.
 *
 * ArcNet's part is the subdomain, the DNS record and the edge. The admin's part happens in Microsoft
 * 365 and Teams PowerShell, which only they can sign in to; this page tells them exactly what to do
 * and records how far they have got.
 */

export function TeamsTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayEdit = can('teams:write');
  const q = useTeams(tenantId);
  const m = useTeamsMutations(tenantId);
  const d = q.data;

  return (
    <>
      <SectionHead title="Microsoft Teams" sub="People make and take calls in Teams on this account's numbers, alongside their phones." />
      {q.error && <ErrorBanner error={q.error} />}
      {!d ? <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 140 }} /></div>
        : !d.available ? (
          <div className="arc-card"><Empty icon="phone" title="Not available yet" hint="Microsoft Teams calling is not switched on for this platform." /></div>
        ) : !d.connection ? (
          <div className="arc-card">
            <CardHead title="Connect Microsoft Teams" />
            <div className="arc-card-body">
              <p className="arc-muted" style={{ marginTop: 0 }}>
                You need a Microsoft 365 Global Administrator for the next steps, and a Teams Phone licence for each person who calls from Teams.
                Starting gives this account its own domain to add to Microsoft 365.
              </p>
              {m.start.error && <ErrorBanner error={m.start.error} />}
              {mayEdit && (
                <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={m.start.isPending} onClick={() => m.start.mutate(undefined)}>
                  {m.start.isPending ? 'Starting…' : 'Start'}
                </button>
              )}
            </div>
          </div>
        ) : <Steps tenantId={tenantId} d={d} conn={d.connection} mayEdit={mayEdit} m={m} />}
    </>
  );
}

type Conn = NonNullable<TeamsConnection['connection']>;
type Mut = ReturnType<typeof useTeamsMutations>;

function Steps({ tenantId, d, conn, mayEdit, m }: { tenantId: string; d: TeamsConnection; conn: Conn; mayEdit: boolean; m: Mut }) {
  const [txt, setTxt] = useState(conn.verify_txt ?? '');
  const [copied, setCopied] = useState(false);
  const err = m.update.error instanceof ApiClientError ? m.update.error : null;
  const resource = `arcnet@${conn.fqdn}`;
  return (
    <div className="arc-stack" style={{ gap: 16 }}>
      <div className="arc-card">
        <CardHead title={conn.fqdn} sub="This account's domain for Teams calling" action={
          conn.status === 'active' ? <Badge tone="ok">calls on</Badge> : conn.status === 'off' ? <Badge tone="muted">off</Badge> : <Badge tone="warn">setting up</Badge>
        } />
        <div className="arc-card-body">
          <ol className="arc-steps" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 14 }}>
            <li>
              <strong>Add the domain in Microsoft 365.</strong>{' '}
              In the Microsoft 365 admin center, go to Settings, Domains, Add domain, and enter <code className="arc-mono">{conn.fqdn}</code>.
              Choose to add a TXT record, and paste the value Microsoft shows here. ArcNet publishes it.
              <form className="arc-row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}
                onSubmit={(e) => { e.preventDefault(); m.update.mutate({ verify_txt: txt.trim() || null }); }}>
                <input className="arc-input arc-mono" style={{ maxWidth: 240 }} placeholder="MS=ms12345678" aria-label="TXT value from Microsoft"
                  value={txt} disabled={!mayEdit} onChange={(e) => setTxt(e.target.value)} />
                {mayEdit && <button type="submit" className="arc-btn arc-btn-sm" disabled={m.update.isPending || txt.trim() === (conn.verify_txt ?? '')}>Save</button>}
                {conn.verify_txt && (conn.txt_published_at
                  ? <Badge tone="ok">published</Badge>
                  : <span className="arc-subtle" style={{ fontSize: 12 }}>waiting for ArcNet to publish it; this shows published when it is</span>)}
              </form>
              {err?.fieldErrors?.['verify_txt'] && <div className="arc-error">{err.fieldErrors['verify_txt']}</div>}
            </li>
            <li>
              <strong>Verify it.</strong>{' '}
              Once it says published, press Verify in Microsoft 365, then tick this.
              <div style={{ marginTop: 8 }}>
                <Switch id="teams-verified" checked={!!conn.verified_at} disabled={!mayEdit || !conn.txt_published_at || m.update.isPending}
                  label="Microsoft has verified the domain" onChange={(v) => m.update.mutate({ verified: v })} />
              </div>
            </li>
            <li>
              <strong>Activate it.</strong>{' '}
              Microsoft only uses a domain once an account signs in on it. Create a resource account named
              {' '}<code className="arc-mono">{resource}</code> and give it a free Microsoft Teams Phone Resource Account licence.
            </li>
            <li>
              <strong>Send calls to ArcNet, and give people their numbers.</strong>{' '}
              Run this in PowerShell as a Teams Administrator, with each person's Microsoft 365 sign-in in place of user@example.com.
              It updates as you add people below.
              <pre className="arc-mono" style={{ marginTop: 8, padding: 12, background: 'var(--arc-surface-2)', borderRadius: 8, overflowX: 'auto', fontSize: 12, maxHeight: 320 }}>{d.script}</pre>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => {
                void navigator.clipboard.writeText(d.script ?? '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
              }}>{copied ? 'Copied' : 'Copy script'}</button>
            </li>
          </ol>
          {m.update.error && !err?.fieldErrors?.['verify_txt'] && <ErrorBanner error={m.update.error} />}
          <p className="arc-subtle" style={{ margin: '14px 0 0', fontSize: 12 }}>
            Last call from Teams: {conn.last_call_in_at ? relativeTime(conn.last_call_in_at) : 'none yet'} ·
            to Teams: {conn.last_call_out_at ? relativeTime(conn.last_call_out_at) : 'none yet'}
          </p>
          {mayEdit && (
            <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
              {conn.status !== 'off'
                ? <button type="button" className="arc-btn arc-btn-sm" onClick={() => {
                  void confirmAction({
                    title: 'Switch off Microsoft Teams calling?',
                    body: 'Calls stop reaching Teams, and nobody can call out from Teams, until you switch it back on. Settings are kept.',
                    action: 'Switch off', danger: true,
                  }).then((ok) => { if (ok) m.update.mutate({ status: 'off' }); });
                }}>Switch off</button>
                : <button type="button" className="arc-btn arc-btn-sm" onClick={() => m.update.mutate({ status: 'setup' })}>Switch on</button>}
              <button type="button" className="arc-btn arc-btn-sm arc-btn-danger" disabled={m.remove.isPending} onClick={() => {
                void confirmAction({
                  title: 'Remove Microsoft Teams calling?',
                  body: 'Calls stop reaching Teams, and every extension’s Teams number is forgotten. Remove the domain from Microsoft 365 too.',
                  action: 'Remove', danger: true,
                }).then((ok) => { if (ok) m.remove.mutate(); });
              }}>Remove</button>
            </div>
          )}
        </div>
      </div>
      <Microsoft365Card tenantId={tenantId} d={d} mayEdit={mayEdit} />
      <Seats tenantId={tenantId} d={d} />
    </div>
  );
}

/**
 * The account's Microsoft 365, connected (teams/microsoft.ts). One click for the client's admin:
 * Microsoft's own consent page, then back here. Connected, ArcNet reads each Teams person's status;
 * the rest of the setup moves into it next (docs/44).
 */
function Microsoft365Card({ tenantId, d, mayEdit }: { tenantId: string; d: TeamsConnection; mayEdit: boolean }) {
  const ms = useTeamsMicrosoft(tenantId);
  const c = d.connection!;
  const connected = !!c.ms_tenant_id;
  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Microsoft 365"
        sub={connected
          ? `Connected ${c.ms_connected_at ? relativeTime(c.ms_connected_at) : ''}. ArcNet shows each person's Teams status.`
          : 'Connect this account\'s Microsoft 365 so ArcNet can show each person\'s Teams status. A Microsoft 365 Global Administrator signs in once and accepts.'}
        action={mayEdit ? (connected ? (
          <button type="button" className="arc-btn arc-btn-sm" disabled={ms.disconnect.isPending}
            onClick={() => void confirmAction({
              title: 'Disconnect Microsoft 365?',
              body: 'ArcNet stops reading Teams status for this account. Calls keep working. To remove the ArcNet for Teams app from your Microsoft 365 entirely, delete it under Enterprise applications in Microsoft Entra.',
              action: 'Disconnect',
            }).then((ok) => { if (ok) ms.disconnect.mutate(); })}>
            Disconnect
          </button>
        ) : (
          <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" disabled={ms.connect.isPending}
            onClick={() => ms.connect.mutate()}>
            {ms.connect.isPending ? 'Opening Microsoft…' : 'Connect Microsoft 365'}
          </button>
        )) : undefined} />
      {connected && (
        <div className="arc-card-body arc-muted" style={{ fontSize: 13 }}>
          Microsoft 365 tenant <span className="arc-mono">{c.ms_tenant_id}</span>
        </div>
      )}
      {(ms.connect.error || ms.disconnect.error) && (
        <div className="arc-card-body"><ErrorBanner error={ms.connect.error ?? ms.disconnect.error} /></div>
      )}
    </div>
  );
}

/**
 * Which extensions ring in Teams, at a glance. Each extension's own Teams settings -- on or off, its
 * number, its Teams sign-in -- are on its Phone setup tab, behind the Microsoft Teams feature (0166;
 * owner, 19 October 2026: "the individual extension configs and enablement should be a feature gated
 * on the extension"). This tab is the account's: the connection and the script.
 */
function Seats({ tenantId, d }: { tenantId: string; d: TeamsConnection }) {
  const status = useTeamsStatus(tenantId, d.seats.length > 0).data;
  const of = (id: string) => status?.seats.find((s) => s.extension_id === id);
  return (
    <div className="arc-card">
      <CardHead title="Ringing in Teams"
        sub="Turn Teams on for an extension, and set its number and Teams sign-in, on that extension's Phone setup tab." />
      {d.seats.length === 0 ? (
        <Empty icon="phone" title="No extensions in Teams yet"
          hint="Add the Microsoft Teams feature to an extension, then set it up on its Phone setup tab." />
      ) : (
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Extension</th><th>Number in Teams</th><th>Teams sign-in</th><th>Rings in Teams</th><th>Now</th></tr></thead>
            <tbody>
              {d.seats.map((s) => (
                <tr key={s.extension_id}>
                  <td>
                    <div className="arc-row" style={{ gap: 10 }}>
                      <span className="arc-ext-num">{s.number}</span>
                      <span style={{ fontWeight: 550 }}>{s.display_name}</span>
                    </div>
                  </td>
                  <td className="arc-mono">{s.teams_number}</td>
                  <td>{s.teams_user ?? <span className="arc-muted">Not set</span>}</td>
                  <td>{s.enabled ? <Badge tone="ok">On</Badge> : <Badge tone="muted">Off</Badge>}</td>
                  <td>
                    {(() => {
                      const st = of(s.extension_id);
                      const problems = st ? teamsProblems(st, !!status?.microsoft_connected) : [];
                      return (
                        <div className="arc-stack" style={{ gap: 4 }}>
                          {status?.microsoft_connected ? <TeamsPresenceBadge seat={st} /> : <span className="arc-muted">Connect Microsoft 365</span>}
                          {problems.map((p) => <span key={p} className="arc-hint" style={{ margin: 0 }}>{p}</span>)}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
